/**
 * UC-25: RequestAiCoaching
 * TOGAF Application ABB — player requests AI coaching tip without UI deps.
 */
import type { IAppLogger } from '../../domain/ports/IAppLogger';
import type { IAiAdvisor } from '../../domain/ports/IAiAdvisor';

export interface RequestAiCoachingInput {
  objective: string;
  aiEnabled: boolean;
}

export interface RequestAiCoachingOutput {
  ok: boolean;
  pendingMessage?: string;
  tip?: string;
  error?: string;
}

export interface RequestAiCoachingDependencies {
  aiAdvisor: IAiAdvisor;
  logger?: IAppLogger;
}

export class RequestAiCoachingUseCase {
  constructor(private readonly deps: RequestAiCoachingDependencies) {}

  async execute(input: RequestAiCoachingInput): Promise<RequestAiCoachingOutput> {
    if (!input.aiEnabled) {
      return { ok: false, error: 'ai_disabled' };
    }

    try {
      const tip = await this.deps.aiAdvisor.generateCoachingTip(input.objective);

      this.deps.logger?.log('debug', 'ai.coaching_requested', {
        objectiveLength: input.objective.length,
        tipLength: tip.length,
      });

      return {
        ok: true,
        pendingMessage: 'Consulting AI Coach...',
        tip,
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.deps.logger?.log('warn', 'ai.coaching_failed', { error: message });
      return { ok: false, error: message };
    }
  }
}